import { ReactNode } from 'react'

import {
  ConnectedService,
  DdosProtectionTypes,
  Location,
  LocationType,
  Money,
  MrcNrc,
  PortTypes,
  ServiceComponentVLANType,
} from '@/lib/definitions/api.types'

export enum IpTransitTabSteps {
  Location = 1,
  Ports = 2,
  IpTransit = 3,
  Summary = 4,
}

export enum IpTransitOverGRETabSteps {
  IpTransit = 1,
  Summary = 2,
}

export type IpTransitStep = keyof typeof IpTransitTabSteps
export type IpTransitOverGREStep = keyof typeof IpTransitOverGRETabSteps

export enum FlexEthernetTabWithoutFlexPeerSteps {
  Endpoints = 1,
  Summary = 2,
}

export enum FlexEthernetTabSteps {
  Product = 1,
  Endpoints = 2,
  Summary = 3,
}

export enum FlexEthernetTabType {
  PointToPoint = 1,
  LocalEndpoint = 2,
  Connection = 3,
}

export enum ProductType {
  FlexEthernet = 1,
  FlexPeer = 2,
}

export type FlexEthernetStep =
  | keyof typeof FlexEthernetTabSteps
  | keyof typeof FlexEthernetTabWithoutFlexPeerSteps

export enum IpAccessTabSteps {
  Location = 1,
  Ports = 2,
  IpAccess = 3,
  Summary = 4,
}

export type IpAccessStep = keyof typeof IpAccessTabSteps

export interface City {
  locations: Location[]
  name: string
  country: string
  activeLocations: number
}

export interface PortSpeed {
  id: number
  speedInMbps: number
  speed: number
  unit: string
  type: string
  name: string
  isLag?: boolean
  link?: string
}

export interface PortSpeedOption extends PortSpeed, MrcNrc {
  attribute_id?: number
}

export interface Prefix {
  id: number
  addresses: number
  isRecommended?: boolean
  note?: string
}

export interface PrefixOption extends Prefix, MrcNrc {}

export interface CommittedDataRateOption {
  speedInMbps: number
  speed: number
  unit: 'Mbps' | 'Gbps'
  nrc?: Money
  mrc?: Money
  burst?: Money
}

export interface FieldTypes {
  key: string
  label: string
  type?: any
  defaultValue?: Function
  suffix?: Function
}

export interface WizardTabItem {
  id: string
  label: string
  headline?: string
}

export interface ContractTerm {
  label: string
  label_short: string
  period: number
}

export type BandwidthOptions = Record<number, number[]>

export interface FlexEthernetEndpoint {
  internalId?: number
  selectedLocation: string
  selectedLocationId?: number
  selectedLocationSpeed: number
  selectedPortSpeed?: PortSpeedOption
  selectedPortType?: string
  selectedPortName: PortTypes | null
  selectedServiceSpeed: number | null
  selectedLocationType: LocationType
  vlanId: number | null | undefined
  vlanType: ServiceComponentVLANType | undefined
  existingPortId?: number
  lag?: LAGProperties
  flexConnectKey?: string
  customerInformation?: {
    name: string
  }
  portName?: string
  portType?: string
}

export interface LagPortInfo {
  id: number
  name: string
  sco?: string
  portType?: string
  serviceSid?: string
}

export interface ExistingPort {
  id: number
  name: string
  locationName: string
  portType: string
  isLag: boolean
  lagCount: number
  portSpeed: PortSpeed
  serviceSid?: string
  sco?: string
  virtualNetworkName?: string
  services?: ConnectedService[]
  lagPorts?: LagPortInfo[]
}

export interface ExistingPortItem extends ExistingPort {
  location: Location
}

export interface PortDetail {
  key: string
  label: string | ReactNode
  value?: ReactNode
  isNrc?: boolean
}

export interface LAGProperties {
  memberCount: number
  name: string
}

export interface GeoCoordinates {
  lat: number
  lng: number
}

export interface GeoCircle {
  center: GeoCoordinates
  radius: number
}

export interface GeoRectangle {
  southWest: GeoCoordinates
  northEast: GeoCoordinates
}

export enum DDoSPrefixType {
  IPv4 = 'IPv4 /24',
  IPv6 = 'IPv6 /48',
}

export enum DDoSPrefixSize {
  FLEXDETECT = 'FlexDetect',
  PLUS = 'Plus',
  PREMIUM = 'Premium',
  ENTERPRISE = 'Enterprise',
}

export interface DDoSPrefix {
  count: number
  type: DDoSPrefixType
  size: DDoSPrefixSize
  term: string
}

export interface DDoSProtectionPricingMrc extends DDoSPrefix {
  cost: number
}

export type DDoSProtectionPricing = Record<string, DDoSProtectionPricingMrc>

export type IpServices = 'ip-access' | 'ip-transit'

export interface DDoSTier {
  tier: number
  value: DdosProtectionTypes
  title: string
  features: TierFeature[]
  price: DDoSProtectionPricing
}

export interface TierFeature {
  name: string
  value: string
}
